import React, { useEffect, useState } from 'react';
import {
  aopApi, agentForWorkflow, agentLabel, formatWhen, workflowLabel, WORKFLOWS,
} from '../aopApi';

export default function ManualTrigger() {
  const [workflow, setWorkflow] = useState('');
  const [opportunityId, setOpportunityId] = useState('');
  const [csdFormId, setCsdFormId] = useState('');
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recentRuns, setRecentRuns] = useState([]);
  const [dryRun, setDryRun] = useState(true);

  const selected = WORKFLOWS.find((w) => w.id === workflow);

  const loadRecent = () => {
    aopApi
      .runs({ limit: 8 })
      .then((res) => res.success && setRecentRuns(res.runs || []))
      .catch(() => {});
  };

  useEffect(() => {
    loadRecent();
    aopApi.status().then((res) => res.success && setDryRun(res.status.dryRun)).catch(() => {});
  }, []);

  const handleTrigger = async () => {
    if (!workflow || (selected.needsEntity && !opportunityId.trim())) return;
    setLoading(true);
    setError(null);
    try {
      const entityRefs = {};
      if (opportunityId.trim()) entityRefs.opportunityId = opportunityId.trim();
      if (csdFormId.trim()) entityRefs.csdFormId = csdFormId.trim();
      const res = await aopApi.enqueue(workflow, entityRefs);
      if (!res.success) throw new Error(res.error || 'Enqueue failed');
      setSubmitted({ workflow: selected.label, entity: opportunityId.trim() || null, jobId: res.jobId });
      setWorkflow('');
      setOpportunityId('');
      setCsdFormId('');
      loadRecent();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="aop-page-header">
        <div>
          <h1 className="aop-page-title">Manual Trigger</h1>
          <p className="aop-page-subtitle">Enqueue a workflow by name and entity ID for ad-hoc or test runs</p>
        </div>
      </div>

      <div className="aop-grid-2" style={{ alignItems: 'start' }}>
        {/* Trigger form */}
        <div className="aop-card">
          <div className="aop-card-header">
            <span className="aop-card-title">Enqueue Workflow</span>
            {dryRun && (
              <span className="aop-badge" style={{ background: 'rgba(249,115,22,0.12)', color: 'var(--orange)' }}>DRY RUN</span>
            )}
          </div>
          <div className="aop-card-body">
            {submitted && (
              <div style={{ background: 'rgba(52,199,89,0.1)', border: '1px solid rgba(52,199,89,0.2)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--green)' }}>
                ✓ Enqueued: <strong>{submitted.workflow}</strong>
                {submitted.entity && <> for <strong>{submitted.entity}</strong></>}
                {' '}(job <code>{submitted.jobId.slice(-8)}</code>). The worker picks it up on its next poll.
              </div>
            )}
            {error && (
              <div style={{ background: 'rgba(255,98,93,0.1)', border: '1px solid rgba(255,98,93,0.25)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--red)' }}>
                {error}
              </div>
            )}

            <div className="aop-form-group">
              <label className="aop-form-label">Workflow</label>
              <select
                className="aop-form-select"
                value={workflow}
                onChange={(e) => { setWorkflow(e.target.value); setOpportunityId(''); setCsdFormId(''); setSubmitted(null); }}
              >
                <option value="">Select a workflow…</option>
                <optgroup label="AOP — Operations">
                  {WORKFLOWS.filter((w) => w.platform !== 'cop').map((w) => (
                    <option key={w.id} value={w.id}>{w.label}</option>
                  ))}
                </optgroup>
                <optgroup label="COP — Campaign Optimization">
                  {WORKFLOWS.filter((w) => w.platform === 'cop').map((w) => (
                    <option key={w.id} value={w.id}>{w.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>

            {selected && selected.needsEntity && (
              <>
                <div className="aop-form-group">
                  <label className="aop-form-label">Opportunity ID</label>
                  <input
                    className="aop-form-input"
                    type="text"
                    placeholder="Salesforce Opportunity ID"
                    value={opportunityId}
                    onChange={(e) => setOpportunityId(e.target.value)}
                  />
                </div>
                <div className="aop-form-group">
                  <label className="aop-form-label">CSD Form ID (optional)</label>
                  <input
                    className="aop-form-input"
                    type="text"
                    placeholder="CSD form ID"
                    value={csdFormId}
                    onChange={(e) => setCsdFormId(e.target.value)}
                  />
                </div>
              </>
            )}

            {selected && (
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 11, color: 'var(--text-sec)' }}>
                <div style={{ marginBottom: 4 }}>
                  <span className={`aop-agent-tag ${selected.agent}`}>{agentLabel(selected.agent)}</span>
                </div>
                {selected.needsEntity
                  ? 'Requires an Opportunity ID. The agent will resolve full context from Salesforce and CSD at runtime.'
                  : 'No entity reference required. The agent will query all active Opportunities independently.'}
              </div>
            )}

            <button
              className="aop-btn trigger"
              onClick={handleTrigger}
              disabled={!workflow || (selected?.needsEntity && !opportunityId.trim()) || loading}
              style={{ opacity: (!workflow || (selected?.needsEntity && !opportunityId.trim())) ? 0.4 : 1, cursor: !workflow ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Enqueueing…' : '▶  Trigger Workflow'}
            </button>

            {dryRun && (
              <div style={{ marginTop: 12, fontSize: 10, color: 'var(--text-dim)', lineHeight: 1.5 }}>
                All triggered runs execute in DRY RUN mode while <code style={{ color: 'var(--orange)' }}>AOP_DRY_RUN=1</code>. No external writes will occur without approval.
              </div>
            )}
          </div>
        </div>

        {/* Recent runs */}
        <div className="aop-card">
          <div className="aop-card-header">
            <span className="aop-card-title">Recent Runs</span>
          </div>
          <div style={{ padding: '8px 0' }}>
            {recentRuns.length === 0 ? (
              <div className="aop-empty">No runs yet</div>
            ) : recentRuns.map((r, i) => (
              <div key={r._id} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: i < recentRuns.length - 1 ? '1px solid var(--border)' : 'none', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{workflowLabel(r.workflow)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
                    {r.entityRefs && r.entityRefs.opportunityId && (
                      <><code style={{ color: 'var(--blue)' }}>{r.entityRefs.opportunityId}</code> · </>
                    )}
                    {r.triggerType} · {formatWhen(r.createdAt)}
                  </div>
                </div>
                <span className={`aop-agent-tag ${agentForWorkflow(r.workflow)}`}>{agentLabel(agentForWorkflow(r.workflow))}</span>
                <span className={`aop-badge ${r.status}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
